import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { type Activity, ActivityApi, type CreateActivityDto } from '@/api-swagger'
import { localCalendarDateKey } from '@/utils/dateUtils'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

/** Tâche renvoyée par GET boost-core /todos/me/daily */
export type SuiteDailyTodo = {
  uuid: string
  title: string
  description?: string | null
  type?: string
  status?: string
  dueDate?: string | null
}

export const useActivitiesStore = defineStore('activities', () => {
  const api = new ActivityApi()

  const activities = ref<Activity[] | null>(null)
  const suiteTodosToday = ref<SuiteDailyTodo[]>([])
  const suiteTodosOverdue = ref<SuiteDailyTodo[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const loadSuiteDailyTodos = async (opts?: { quiet?: boolean }): Promise<boolean> => {
    resetValues()
    const suiteStore = useSuiteStore()
    const dateKey = localCalendarDateKey(new Date())
    if (!dateKey) {
      if (!opts?.quiet) error.value = 'Date locale invalide.'
      return false
    }
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(
        `todos/me/daily?date=${encodeURIComponent(dateKey)}`,
      )
      if (!res || !res.ok) {
        if (!opts?.quiet) {
          error.value = 'Impossible de charger les tâches'
        }
        return false
      }
      const json = (await res.json()) as {
        today?: SuiteDailyTodo[]
        overdue?: SuiteDailyTodo[]
      }
      suiteTodosToday.value = json.today ?? []
      suiteTodosOverdue.value = json.overdue ?? []
      return true
    } catch (err) {
      console.error(err)
      if (!opts?.quiet) {
        error.value = 'Impossible de charger les tâches (Boost Core / Prisma).'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  const completeSuiteTodo = async (uuid: string) => {
    const suiteStore = useSuiteStore()
    loading.value = true
    try {
      const res = await suiteStore.suiteFetch(`todos/${uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'COMPLETED' }),
      })
      if (!res || !res.ok) {
        error.value = 'Impossible de marquer la tâche comme terminée.'
        return
      }
      await loadSuiteDailyTodos()
    } catch (err) {
      console.error(err)
      error.value = 'Impossible de marquer la tâche comme terminée.'
    } finally {
      loading.value = false
    }
  }

  const getActivitiesByUserId = async (userId: number) => {
    resetValues()
    if (!userId) return
    await withApiState(
      async () => {
        activities.value = await api.getPartnerCallsActivitiesByUserId({ userId })
      },
      { loading, error, errorText: 'Impossible de charger les To Dos.', logLabel: 'Erreur lors de la récupération des To Dos :' },
    )
  }

  const createActivityForUser = async (userId: number, createActivityDto: CreateActivityDto) => {
    await withApiState(
      async () => {
        await api.createPartnerCallsActivitiesByUserId({ userId, createActivityDto })
      },
      { loading, error, errorText: "La création de l'activité a échoué.", logLabel: "Erreur lors de la création de l'activité :" },
    )
  }

  const updateActivity = async (activityId: number, updatedData: object) => {
    await withApiState(
      async () => {
        await api.updatePartnerActivity({ activityId, updateActivityDto: updatedData })
      },
      { loading, error, errorText: "La modification de l'activité a échoué.", logLabel: "Erreur lors de la modification de l'activité :" },
    )
  }

  const deleteActivity = async (activityId: number) => {
    await withApiState(
      async () => {
        await api.deletePartnerActivity({ activityId })
        activities.value = activities.value?.filter((activity) => activity.id !== activityId) || []
      },
      { loading, error, errorText: "La suppression de l'activité a échoué.", logLabel: "Erreur lors de la suppression de l'activité :" },
    )
  }
  const bulkDeleteActivities = async (activityIds: number[]) => {
    await withApiState(
      async () => {
        await api.deletePartnerActivities({ deletePartnerActivitiesRequest: { activityIds } })
        activities.value = activities.value?.filter((activity) => !activityIds.includes(activity.id)) || []
      },
      { loading, error, errorText: 'La suppression des activités a échoué.', logLabel: 'Erreur lors de la suppression des activités :' },
    )
  }

  const resetValues = () => {
    activities.value = []
    suiteTodosToday.value = []
    suiteTodosOverdue.value = []
    loading.value = false
    error.value = null
  }

  const todayActivities = computed(() => {
    if (!activities.value) return []
    const todayKey = localCalendarDateKey(new Date())
    if (!todayKey) return []

    return activities.value.filter((activity) => {
      const key = localCalendarDateKey(activity.dateDeadline)
      return key !== null && key === todayKey
    })
  })

  const outdatedActivities = computed(() => {
    if (!activities.value) return []
    const todayKey = localCalendarDateKey(new Date())
    if (!todayKey) return []

    return activities.value.filter((activity) => {
      const key = localCalendarDateKey(activity.dateDeadline)
      return key !== null && key < todayKey
    })
  })

  return {
    activities,
    suiteTodosToday,
    suiteTodosOverdue,
    loading,
    error,
    todayActivities,
    outdatedActivities,
    getActivitiesByUserId,
    loadSuiteDailyTodos,
    completeSuiteTodo,
    createActivityForUser,
    updateActivity,
    deleteActivity,
    bulkDeleteActivities,
    resetValues,
  }
})
